import React, { useEffect, useState } from "react";
import { InputOtp } from "primereact/inputotp";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ArrowLeft, Moon, Sun } from "@phosphor-icons/react";
import { useTheme } from "../../../theme";
import authBrandIcon from "../../../assets/design/HallTicket logo/app-icon/square-dark/hallticket-icon-256-dark.png";
import "./OtpVerification.css";

interface OtpVerificationProps {
  mobile: string;
  onVerify: (otp: string) => void | Promise<void>;
  onBack: () => void;
  /** Error from parent (e.g. wrong OTP from Firebase). Cleared when user edits the code. */
  verificationError?: string | null;
  /** Called when user changes the OTP input so parent can clear verificationError */
  onClearVerificationError?: () => void;
  /** For Storybook: initial OTP value to show a pre-filled state */
  initialToken?: string;
  /** For Storybook: initial error message to show a validation state */
  initialError?: string | null;
  /** Triggered when user clicks "Resend code" */
  onResend?: () => void | Promise<void>;
  /** Whether a resend request is currently in progress */
  isResending?: boolean;
  /** Whether OTP verification is currently in progress */
  isVerifying?: boolean;
}

const MASKED_MOBILE = (mobile: string) => {
  if (mobile.length < 4) return "****";
  return "******" + mobile.slice(-4);
};
const RESEND_COOLDOWN_SECONDS = 30;

export function OtpVerification({
  mobile,
  onVerify,
  onBack,
  verificationError = null,
  onClearVerificationError,
  initialToken = "",
  initialError = null,
  onResend,
  isResending = false,
  isVerifying = false,
}: OtpVerificationProps) {
  const { mode, toggleTheme } = useTheme();
  const [token, setToken] = useState<string>(initialToken);
  const [error, setError] = useState<string | null>(initialError);
  const [resendCooldown, setResendCooldown] = useState<number>(onResend ? RESEND_COOLDOWN_SECONDS : 0);

  const displayError = verificationError ?? error;
  const isVerifyDisabled = token.replace(/\s/g, "").length < 6;

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }

    const timerId = window.setInterval(() => {
      setResendCooldown((previous) => {
        if (previous <= 1) {
          window.clearInterval(timerId);
          return 0;
        }
        return previous - 1;
      });
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [resendCooldown]);

  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    onClearVerificationError?.();
    const trimmed = token.replace(/\s/g, "");
    if (!trimmed) {
      setError("Please enter the verification code.");
      return;
    }
    if (trimmed.length < 6) {
      setError("Please enter the complete code.");
      return;
    }
    void onVerify(trimmed);
  };

  const handleTokenChange = (e: { value?: string | number | null }) => {
    setToken(String(e.value ?? ""));
    if (verificationError) {
      onClearVerificationError?.();
    }
  };

  const handleResendClick = () => {
    if (!onResend || isResending || resendCooldown > 0) {
      return;
    }

    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    void onResend();
  };

  return (
    <div className="auth-screen">
      <button type="button" className="theme-toggle" onClick={toggleTheme}>
        {mode === "dark" ? <Sun size={15} weight="bold" /> : <Moon size={15} weight="bold" />}
      </button>
      <div className="auth-card">
        <button type="button" className="back-link" onClick={onBack}>
          <ArrowLeft size={14} weight="bold" /> Back
        </button>
        <div className="brand">
          <div className="brand-icon"><img src={authBrandIcon} alt="HallTicket" className="brand-icon-img" /></div>
          <div className="brand-name">
            Hall<span>Ticket</span>
          </div>
        </div>
        <h2 className="auth-title">Enter OTP</h2>
        <p className="auth-sub">
          We sent a 6-digit code to <strong>{MASKED_MOBILE(mobile)}</strong>
        </p>
        <form onSubmit={handleVerify} className="otp-verification__form">
          <div className="field-label">Verification Code</div>
          <div className="otp-verification__otp-wrap">
            <InputOtp value={token} onChange={handleTokenChange} length={6} integerOnly className="otp-verification__otp" />
          </div>
          <div className="otp-verification__actions">
            {onResend && (
              <Button
                type="button"
                label={isResending ? "Sending..." : resendCooldown > 0 ? `Resend OTP (${resendCooldown}s)` : "Resend OTP"}
                link
                className="otp-verification__resend"
                onClick={handleResendClick}
                disabled={isResending || resendCooldown > 0}
              />
            )}
          </div>
          <Button type="submit" label="Verify & Continue" className="btn btn-primary otp-verification__button" disabled={isVerifyDisabled || isVerifying} loading={isVerifying} />
          {displayError && <Message severity="error" text={displayError} className="otp-verification__message" />}
        </form>
      </div>
    </div>
  );
}
